import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Button,
  InputBoolean,
  P,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { VendorCatalogItem } from '../../../../Lib/Hooks/ItemService';
import {
  UpdateKeyDetailsInputs,
  updateKeyDetailsSchema,
} from '../../schemas/updateKeyDetails';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { useMerchHierarchyOptions } from '../../hooks/useMerchHierarchyOptions.hooks';
import { useGetMetaDatas } from '../../../../Lib/Hooks/MetadataService';
import { ImageFileInput } from '../../../ReusableComponents/ImageFileInput/ImageFileInput';
import { CountryListSelectInputOptions } from '../../../../Lib/Constants/countries';
import { useUpdateKeyDetails } from '../../../../Lib/Hooks/ItemService/useUpdateKeyDetails';
import { useGetItemPhotoURLs } from '../../../../Lib/Hooks/ItemService/useGetItemPhotoURLs';

const mapItemToKeyDetailsFormValues = (
  item: VendorCatalogItem,
): UpdateKeyDetailsInputs => ({
  division: item.division ?? '',
  department: item.department ?? '',
  category: item.category ?? '',
  class: item.class ?? '',
  style_number: item.style_number ?? '',
  jr_cost: Number(item.jr_cost) ?? 0,
  plus_cost: item.plus_cost !== null ? Number(item.plus_cost) : null,
  fashionnova_exclusive: item.fashionnova_exclusive ?? false,
  fit_sample: item.fit_sample ?? false,
  lead_time_in_weeks: item.lead_time_in_weeks ?? 0,
  country_of_origin: item?.draft_purchase_order?.country_of_origin ?? '',
});

type ItemKeyDetailsEditFormProps = {
  onCloseForm: () => void;
  item: VendorCatalogItem;
};

export function ItemKeyDetailsEditForm({
  onCloseForm,
  item,
}: ItemKeyDetailsEditFormProps) {
  const { id: currentUserId } = useParams<{
    id: string;
  }>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newItemSwatchPhoto, setNewItemSwatchPhoto] = useState<File | null>(
    null,
  );
  const [newItemFrontPhoto, setNewItemFrontPhoto] = useState<File | null>(null);

  const { data: metadatas } = useGetMetaDatas(['ns_master_vendorsMerchType']);
  const { mutate: updateKeyDetails } = useUpdateKeyDetails();
  const { data: photoUrls } = useGetItemPhotoURLs(currentUserId, item.id);

  const vendorsMerchTypeMetadatas = metadatas?.ns_master_vendorsMerchType;

  const form = useForm({
    resolver: zodResolver(updateKeyDetailsSchema),
    defaultValues: mapItemToKeyDetailsFormValues(item),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const { control, formState, reset, setValue } = form;
  const { errors } = formState;

  const currentDivision = useWatch({ control, name: 'division' });
  const currentDepartment = useWatch({ control, name: 'department' });
  const currentCategory = useWatch({ control, name: 'category' });
  const currentClass = useWatch({ control, name: 'class' });

  const {
    divisionsOptions,
    categoriesOptions,
    departmentOptions,
    classOptions,
  } = useMerchHierarchyOptions({
    currentDivision,
    currentCategory,
    currentDepartment,
    currentClass,
    setDepartmentValue: (value) => setValue('department', value),
    setCategoryValue: (value) => setValue('category', value),
    setClassValue: (value) => setValue('class', value),
  });

  const vendor = useMemo(() => {
    if (vendorsMerchTypeMetadatas) {
      const vendorFound = vendorsMerchTypeMetadatas.find(
        (vendorMerchType) =>
          vendorMerchType.vendorId === item.vendor_internal_id,
      );
      return vendorFound ?? null;
    }
    return null;
  }, [vendorsMerchTypeMetadatas, item.vendor_internal_id]);

  const countryOfOriginOptions = useMemo(() => {
    if (!vendor?.domesticMerchPoEligible) {
      return CountryListSelectInputOptions.filter(
        (country) => country.value !== 'US',
      );
    }
    return CountryListSelectInputOptions;
  }, [vendor]);

  const itemSwatchPhotoThumbnailUrl = photoUrls?.swatch_photo?.thumbnail_url;
  const itemSwatchPhotoUrl = photoUrls?.swatch_photo?.url;
  const itemFrontPhotoThumbnailUrl = photoUrls?.front_photo?.thumbnail_url;
  const itemFrontPhotoUrl = photoUrls?.front_photo?.url;

  const handleSubmit = (data: UpdateKeyDetailsInputs) => {
    setIsSubmitting(true);
    updateKeyDetails(
      {
        itemId: item.id,
        userId: currentUserId,
        updates: {
          ...data,
          front_file: newItemFrontPhoto ?? undefined,
          swatch_file: newItemSwatchPhoto ?? undefined,
        },
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onCloseForm();
        },
        onError: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  useEffect(() => {
    reset(mapItemToKeyDetailsFormValues(item));
  }, [item]);

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
      <Stack direction="col" className="gap-7">
        <Stack
          direction="col"
          gap="sm"
          className="border rounded-xl p-5"
          width="full"
        >
          <ControlledFormInput
            inputType="multiselect"
            name="division"
            control={control}
            label="Product Classification"
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: divisionsOptions,
            }}
            disabled={divisionsOptions.length <= 0}
            required
          />
          <ControlledFormInput
            inputType="multiselect"
            name="department"
            control={control}
            label="Department"
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: departmentOptions,
            }}
            disabled={departmentOptions.length <= 0}
            required
          />
          <ControlledFormInput
            inputType="multiselect"
            name="category"
            control={control}
            label="Category"
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: categoriesOptions,
            }}
            disabled={categoriesOptions.length <= 0}
            required
          />
          <ControlledFormInput
            inputType="multiselect"
            name="class"
            control={control}
            label="Class"
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: classOptions,
            }}
            disabled={classOptions.length <= 0}
            required
          />
        </Stack>
        <Stack
          direction="col"
          gap="lg"
          className="border rounded-xl p-5"
          width="full"
        >
          <Stack direction="col" gap="sm">
            <P size="xsm" weight="medium">
              Add Product Image <span className="nu-text-danger-700">*</span>
            </P>
            <ImageFileInput
              error={
                !itemFrontPhotoThumbnailUrl &&
                !itemFrontPhotoUrl &&
                !newItemFrontPhoto
              }
              id="front_photo"
              baseFileSrc={itemFrontPhotoThumbnailUrl}
              baseFileFallbackSrc={itemFrontPhotoUrl}
              onFileSelect={(file) => {
                setNewItemFrontPhoto(file);
                setValue('front_content_type', file.type);
              }}
              name="front"
            />
          </Stack>
          <Stack direction="col" gap="sm">
            <P size="xsm" weight="medium">
              Add Swatch Photo
            </P>
            <ImageFileInput
              error={false}
              id="swatch_photo"
              baseFileSrc={itemSwatchPhotoThumbnailUrl}
              baseFileFallbackSrc={itemSwatchPhotoUrl}
              onFileSelect={(file) => {
                setNewItemSwatchPhoto(file);
                setValue('swatch_content_type', file.type);
              }}
              name="swatch"
            />
          </Stack>
          <ControlledFormInput
            inputType="text"
            name="style_number"
            control={control}
            label="Style #"
            inputProps={{
              className: 'w-full',
              placeholder: 'Enter the style number',
            }}
            required
          />
        </Stack>
        <Stack
          direction="row"
          gap="sm"
          className="border rounded-xl p-5"
          width="full"
        >
          <ControlledFormInput
            inputType="currency"
            name="jr_cost"
            control={control}
            label="JR Cost"
            required
            inputProps={{
              className: 'w-full',
              inputClassName: '!ring-0',
              placeholder: '0.00',
            }}
            rules={{
              valueAsNumber: true,
            }}
          />
          <ControlledFormInput
            inputType="currency"
            name="plus_cost"
            control={control}
            label="Plus Cost"
            inputProps={{
              className: 'w-full',
              inputClassName: '!ring-0',
              placeholder: '0.00',
            }}
            rules={{
              setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
            }}
          />
        </Stack>
        <Stack
          direction="col"
          gap="sm"
          className="border rounded-xl p-5"
          width="full"
        >
          <Stack direction="col" gap="md">
            <Stack direction="row" content="start">
              <P size="xsm" weight="medium">
                Sample Details <span className="nu-text-danger-700">*</span>
              </P>
            </Stack>
            <P size="xs" color="disabled">
              FN Exclusive: Product is only being sold to Fashion Nova.
              <br />
              Fit Sample: Can be used as a fit sample
            </P>
          </Stack>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
            <P size="xs">FN Exclusive</P>
            <Controller
              name="fashionnova_exclusive"
              control={control}
              render={({ field }) => (
                <InputBoolean
                  checked={field.value}
                  onChangeValue={(val: boolean) => {
                    if (val === undefined) {
                      field.onChange(false);
                    } else {
                      field.onChange(val);
                    }
                  }}
                  error={!!errors?.fashionnova_exclusive}
                  name="fashionnova_exclusive"
                />
              )}
            />
            <P size="xs">Fit Sample</P>
            <Controller
              name="fit_sample"
              control={control}
              render={({ field }) => (
                <InputBoolean
                  checked={field.value}
                  onChangeValue={(val: boolean) => {
                    if (val === undefined) {
                      field.onChange(false);
                    } else {
                      field.onChange(val);
                    }
                  }}
                  error={!!errors?.fit_sample}
                  name="fit_sample"
                />
              )}
            />
          </div>
        </Stack>
        <Stack
          direction="col"
          gap="lg"
          className="border rounded-xl p-5"
          width="full"
        >
          <ControlledFormInput
            inputType="number"
            name="lead_time_in_weeks"
            control={control}
            label="Custom Cut Lead Time"
            inputProps={{
              endAdornment: (
                <P color="disabled" size="xs">
                  weeks
                </P>
              ),
              placeholder: '0',
              className: 'w-full',
              inputClassName: '!ring-0',
            }}
            required
          />
          <ControlledFormInput
            inputType="multiselect"
            name="country_of_origin"
            control={control}
            label="COO"
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: countryOfOriginOptions,
            }}
            required
          />
        </Stack>
        <Stack direction="row" gap="xs" content="end">
          <Button
            color="secondary"
            onClick={onCloseForm}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save Changes
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
